import { useCallback, useEffect, useState } from 'react';

import { MemoryList, listMemory } from './memoryApi';

/**
 * The Memory list for a chat: one read, shared by the banner and the Memory view so a
 * proposal approved in one is gone from the other without a second request.
 *
 * Read while `enabled`, again whenever `refreshKey` changes (the chat bumps it after a
 * turn, which is when the agent proposes), and on `reload` after a change made here.
 * A failed read keeps whatever was shown and reports the error; it never throws.
 */
export function useMemory(
  tenant: string,
  modelId: string | null,
  enabled: boolean,
  refreshKey = 0
) {
  const [list, setList] = useState<MemoryList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  // A different subject is a different list: never show one model's memory on another.
  useEffect(() => {
    setList(null);
    setError(null);
  }, [tenant, modelId]);

  useEffect(() => {
    if (!enabled || !tenant) return undefined;
    let live = true;
    listMemory(tenant, modelId)
      .then((next) => {
        if (!live) return;
        setList(next);
        setError(null);
      })
      .catch((e: unknown) => {
        if (live)
          setError(e instanceof Error ? e.message : 'Memory could not load.');
      });
    return () => {
      live = false;
    };
  }, [enabled, tenant, modelId, refreshKey, version]);

  const reload = useCallback(() => setVersion((v) => v + 1), []);

  return { list, error, reload };
}
